"use client";

import { useEffect, useRef } from "react";
import round from "lodash/round";
import { usePathname } from "next/navigation";
import { useReportWebVitals } from "next/web-vitals";

import { useSelector } from "@/redux/hooks";
import { getUserAgent } from "@/redux/selectors/userAgent";
import { getSessionStorage } from "@/utils/sessionStorage";

const SAMPLE_RATE = 0.6;
export const SESSION_STORE_KEY = "CWV_SAMPLING_RATE";

// connection should exist, but TS doesn't know about it for some reason
type NavigatorWithConnection = Navigator & {
  connection: { effectiveType: "slow-2g" | "2g" | "3g" | "4g" };
};

export type CoreWebVitalsData = {
  deviceType: string;
  name: string;
  network?: string;
  page?: string;
  value: number;
};

const DISPENSARIES_PAGES = ["/cbd-stores", "/medical-marijuana-doctors"];
const DISPENSARY_INFO_PAGES = ["/cannabis-store", "/cbd-store"];

const queue = new Set<CoreWebVitalsData>();
// Generate a random sample for the user
const sampleRate = Number(
  getSessionStorage(SESSION_STORE_KEY, Math.random().toString(), true),
);
const useCoreWebVitals = () => {
  const { isMobile } = useSelector(getUserAgent);
  const pathname = usePathname() || "";

  const documentRef = useRef<Document | null>(
    typeof document !== "undefined" ? document : null,
  );

  const shouldSampleRef = useRef(sampleRate <= SAMPLE_RATE);

  const flushQueue = () => {
    if (queue.size > 0) {
      const body = JSON.stringify([...queue]);

      const beaconResponse = navigator?.sendBeacon?.(
        "/api/metrics/core-web-vitals",
        body,
      );
      if (!beaconResponse) {
        fetch("/api/metrics/core-web-vitals", {
          body,
          keepalive: true,
          method: "POST",
        });
      }

      queue.clear();
    }
  };

  const addToQueue: Parameters<typeof useReportWebVitals>[0] = (metric) => {
    let page = pathname;

    if (
      shouldSampleRef.current === false || // if they aren't part of the sample
      ["", "400", "404", "500"].includes(page) || // if it's an error page
      !["CLS", "INP", "LCP"].includes(metric.name) // if it's not a core web vital metric we care about
    ) {
      return;
    }

    // These pages all use the same layout, so grouping them all together will
    // give us a better idea of how they're performing
    const isDispensariesPage = DISPENSARIES_PAGES.find((dispensaryPage) =>
      page?.startsWith(dispensaryPage),
    );
    const isDispensaryInfoPage = DISPENSARY_INFO_PAGES.find(
      (dispensaryInfoPage) => page?.startsWith(dispensaryInfoPage),
    );
    if (isDispensariesPage) {
      page = page.replace(isDispensariesPage, "/dispensaries");
    } else if (isDispensaryInfoPage) {
      page = page.replace(isDispensaryInfoPage, "/dispensary-info");
    }

    queue.add({
      deviceType: isMobile ? "mobile" : "desktop",
      name: metric.name,
      network: (navigator as NavigatorWithConnection)?.connection
        ?.effectiveType,
      page,
      value: round(
        metric.name === "LCP" ? metric.delta / 1000 : metric.delta,
        3,
      ),
    });
  };

  useReportWebVitals(addToQueue);

  // https://www.npmjs.com/package/web-vitals#batch-multiple-reports-together
  useEffect(() => {
    if (!(documentRef.current && documentRef.current.visibilityState)) {
      return;
    }

    const visibilityChangeHandler = () => {
      if (
        documentRef.current &&
        documentRef.current.visibilityState === "hidden"
      ) {
        flushQueue();
      }
    };

    documentRef.current.addEventListener(
      "visibilitychange",
      visibilityChangeHandler,
    );

    return () => {
      if (documentRef.current) {
        documentRef.current.removeEventListener(
          "visibilitychange",
          visibilityChangeHandler,
        );
      }
    };
  }, [documentRef]);
};

export default useCoreWebVitals;
